import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import toast from 'react-hot-toast';
import { classifyPhone, validateSearch, unidentifiedReplies, UNIDENTIFIED_DAYS } from '../utils/prospects';
import { phoneDigits, phoneKey } from '../utils/customers';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];
const POLL_MS = 15000;
const MAX_ACTIVE_SEARCHES = 3; // evita encolar decenas de búsquedas (cada una gasta cuota de Google)

const isMissingTable = (error) =>
  ['42P01', 'PGRST205'].includes(error?.code) || /prospect/i.test(error?.message || '');

// Prospección (Fase 1): búsquedas en Google Maps y lista de negocios encontrados. n8n toma las búsquedas en
// estado 'pendiente', consulta Google y guarda los resultados; aquí solo se piden, se revisan y se clasifican.
export const useProspects = () => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';
  const isMaster = userRole === 'master';

  const [prospects, setProspects] = useState([]);
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [lidMessages, setLidMessages] = useState([]);
  const [lidCustomers, setLidCustomers] = useState([]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!activeBusinessId) { setLoading(false); return; }
    if (!quiet) setLoading(true);
    const business = String(activeBusinessId);
    const [found, runs] = await Promise.all([
      supabase.from('prospects').select('*').eq('business_id', business).order('created_at', { ascending: false }),
      supabase.from('prospect_searches').select('*').eq('business_id', business).order('created_at', { ascending: false }).limit(20)
    ]);
    const error = found.error || runs.error;
    if (error) {
      setTableMissing(isMissingTable(error));
      setProspects([]);
      setSearches([]);
    } else {
      setTableMissing(false);
      setProspects(Array.isArray(found.data) ? found.data : []);
      setSearches(Array.isArray(runs.data) ? runs.data : []);
    }
    setLoading(false);

    // Respuestas sin teléfono (@lid) de los últimos días y el nombre de perfil con el que escribieron. Si falla, la lista queda vacía.
    try {
      const since = new Date(Date.now() - UNIDENTIFIED_DAYS * 86400000).toISOString();
      const [msgs, people] = await Promise.all([
        supabase.from('customer_messages').select('lid, body, sent_at').eq('business_id', business).eq('direction', 'in').is('phone', null).gte('sent_at', since).order('sent_at', { ascending: false }).limit(200),
        supabase.from('customers').select('lid, name').eq('business_id', business).not('lid', 'is', null).limit(500)
      ]);
      setLidMessages(Array.isArray(msgs?.data) ? msgs.data : []);
      setLidCustomers(Array.isArray(people?.data) ? people.data : []);
    } catch {
      setLidMessages([]);
      setLidCustomers([]);
    }
  }, [activeBusinessId]);

  useEffect(() => { load(); }, [load]);

  // Mientras n8n trabaja una búsqueda, se refresca solo para mostrar los resultados al terminar.
  const hasActiveSearch = searches.some(s => s.status === 'pendiente' || s.status === 'procesando');
  useEffect(() => {
    if (!hasActiveSearch) return undefined;
    const timer = setInterval(() => load({ quiet: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [hasActiveSearch, load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) {
      toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.');
      return true;
    }
    return false;
  };

  const createSearch = async ({ query, city, maxResults }) => {
    if (guardDemo()) return false;
    const check = validateSearch({ query, city, maxResults });
    if (!check.ok) { toast.error(check.error); return false; }
    if (searches.filter(s => s.status === 'pendiente' || s.status === 'procesando').length >= MAX_ACTIVE_SEARCHES) {
      toast.error('Ya tienes búsquedas en curso. Espera a que terminen.');
      return false;
    }
    const { data, error } = await supabase
      .from('prospect_searches')
      .insert([{ business_id: String(activeBusinessId), ...check.value }])
      .select()
      .single();
    if (error) {
      toast.error(isMissingTable(error)
        ? 'Falta ejecutar la migración 20260930g_prospecting.sql en Supabase.'
        : 'No se pudo crear la búsqueda.');
      return false;
    }
    setSearches(prev => [data, ...prev]);
    toast.success('Búsqueda en cola. Cuando n8n la procese, los resultados aparecen aquí.');
    return true;
  };

  // `botMode`: la persona ya habló contigo o te escribió por su cuenta. Se guarda como "Contactado" con WhatsApp confirmado
  // (escribió desde ahí) y fuera de la cola de envío: el bot solo reconoce como prospecto a quien no está en "Nuevo".
  const addProspect = async ({ name, phone, category, address, city, notes, botMode = false }) => {
    if (guardDemo()) return false;
    if (!String(name ?? '').trim()) { toast.error('Escribe el nombre del negocio.'); return false; }
    const parsed = phone ? classifyPhone(phone) : { digits: '', kind: 'invalid' };
    if (phone && parsed.kind === 'invalid') { toast.error('Ese teléfono no es válido (ej. 300 123 4567).'); return false; }
    if (botMode && parsed.kind !== 'mobile') { toast.error('Para que el bot lo atienda necesitas el celular de WhatsApp de la persona.'); return false; }

    // Si ese celular ya está en la lista no se duplica: se pasa a modo prospecto el que ya existe.
    const existing = botMode ? prospects.find(p => p.phone && phoneKey(p.phone) === phoneKey(parsed.digits)) : null;
    if (existing) {
      if (existing.status === 'no_contactar') {
        toast.error('Esa persona pidió no ser contactada: el bot no le responderá como prospecto.');
        return false;
      }
      if (existing.status === 'nuevo' && !(await updateProspect(existing.id, { status: 'contactado', whatsapp: 'yes' }))) return false;
      toast.success(`${existing.name} ya estaba en tu lista: el bot lo atiende como prospecto.`);
      return true;
    }

    const { data, error } = await supabase
      .from('prospects')
      .insert([{
        business_id: String(activeBusinessId), name: name.trim(), phone: parsed.digits || null,
        category: category?.trim() || null, address: address?.trim() || null, city: city?.trim() || null,
        notes: notes?.trim() || null, source: 'manual',
        status: botMode ? 'contactado' : 'nuevo',
        // Un fijo no tiene WhatsApp: se marca de una vez para no gastar una verificación.
        whatsapp: botMode ? 'yes' : (parsed.kind === 'landline' ? 'no' : 'unknown')
      }])
      .select()
      .single();
    if (error) {
      toast.error(error.code === '23505' ? 'Ese negocio o teléfono ya está en tu lista.' : 'No se pudo guardar el prospecto.');
      return false;
    }
    setProspects(prev => [data, ...prev]);
    toast.success(botMode ? 'Guardado: el bot lo atiende como prospecto desde su próximo mensaje.' : 'Prospecto guardado');
    return true;
  };

  // Edita los datos de un prospecto. El teléfono solo se cambia mientras sigue en "Nuevo": si ya se le escribió, o pidió la baja,
  // cambiarlo dejaría libre su número anterior y una búsqueda futura podría volver a proponerlo. Al cambiarlo, el WhatsApp
  // vuelve a "sin verificar" (el envío no sale a un número sin confirmar).
  const editProspect = async (id, { name, phone, category, address, city, notes, is_test }) => {
    if (guardDemo()) return false;
    const current = prospects.find(p => p.id === id);
    if (!current) return false;
    if (!String(name ?? '').trim()) { toast.error('Escribe el nombre del negocio.'); return false; }
    const patch = {
      name: name.trim(), category: category?.trim() || null, address: address?.trim() || null,
      city: city?.trim() || null, notes: notes?.trim() || null
    };
    const typed = String(phone ?? '').trim();
    if (phoneDigits(typed) !== String(current.phone ?? '')) {
      if (current.status !== 'nuevo') { toast.error('El teléfono solo se puede cambiar mientras el prospecto está en estado Nuevo.'); return false; }
      const parsed = typed ? classifyPhone(typed) : { digits: '', kind: 'invalid' };
      if (typed && parsed.kind === 'invalid') { toast.error('Ese teléfono no es válido (ej. 300 123 4567).'); return false; }
      patch.phone = parsed.digits || null;
      patch.whatsapp = parsed.kind === 'landline' ? 'no' : 'unknown';
      patch.whatsapp_checked_at = null;
    }
    // Número de pruebas: solo lo marca el administrador (el bot lo atiende sin el límite de 24 h ni el bloqueo por baja).
    if (isMaster && typeof is_test === 'boolean') patch.is_test = is_test;
    patch.updated_at = new Date().toISOString();
    const previous = prospects;
    setProspects(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p)));
    const { error } = await supabase.from('prospects').update(patch).eq('id', id);
    if (error) {
      setProspects(previous);
      toast.error(error.code === '23505' ? 'Ese teléfono o negocio ya está en tu lista.' : 'No se pudo guardar el cambio. Intenta de nuevo.');
      return false;
    }
    toast.success('Prospecto actualizado.');
    return true;
  };

  const updateProspect = async (id, changes) => {
    if (guardDemo()) return false;
    const previous = prospects;
    const patch = { ...changes, updated_at: new Date().toISOString() };
    setProspects(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p)));
    const { error } = await supabase.from('prospects').update(patch).eq('id', id);
    if (error) {
      setProspects(previous);
      toast.error('No se pudo guardar el cambio. Intenta de nuevo.');
      return false;
    }
    return true;
  };

  // Quien pidió no ser contactado se conserva: si se borrara, una búsqueda futura lo volvería a proponer.
  const deleteProspect = async (id) => {
    if (guardDemo()) return false;
    const target = prospects.find(p => p.id === id);
    if (target?.status === 'no_contactar') {
      toast.error('Los prospectos "No contactar" se conservan para no volver a escribirles.');
      return false;
    }
    const { error } = await supabase.from('prospects').delete().eq('id', id);
    if (error) { toast.error('No se pudo eliminar el prospecto.'); return false; }
    setProspects(prev => prev.filter(p => p.id !== id));
    toast.success('Prospecto eliminado');
    return true;
  };

  const unidentified = useMemo(() => unidentifiedReplies(lidMessages, lidCustomers, prospects), [lidMessages, lidCustomers, prospects]);

  // Vincula una respuesta sin teléfono con un prospecto: desde ahí el bot reconoce a esa persona como prospecto.
  const linkLid = async (prospectId, lid) => {
    if (!prospectId || !lid) return false;
    const ok = await updateProspect(prospectId, { lid: String(lid) });
    if (ok) toast.success('Listo: los próximos mensajes de esa persona se atienden como prospecto.');
    return ok;
  };

  return {
    prospects, searches, loading, tableMissing, isReadOnlyDemo, isMaster, hasActiveSearch,
    unidentified, reload: load, createSearch, addProspect, updateProspect, editProspect, deleteProspect, linkLid
  };
};
